/**
 * A small modal dialog for the Reach (M3 plan §2.6.6): title, body text and a row of buttons, answered with the
 * chosen button's id. Keyboard, mouse and gamepad all drive it through the pure `ModalModel`.
 *
 * - Keys: ←/↑ and Shift+Tab move left, →/↓ and Tab move right, Enter/Space choose, Esc = the last button (ignored
 *   when `dismissable` is false).
 * - Pad: D-pad or left stick move, A chooses, B = the last button (M1's `stepPadNav`, disarmed on open so the press
 *   that opened the dialog does not also answer it).
 * - Mouse: hover focuses, click chooses.
 *
 * With a `UiState` the dialog holds one `ui.modal` count and one Esc handler while open (flight input zeroed, no
 * pointer-lock pause; P or pad Start reach it through the loop's Esc stack), and calls `ui.relock()` from the closing
 * gesture. Without one (boot-time dialogs, before the game context exists) it is self-contained.
 *
 * DOM: the dialog layer is inserted right after `root` (`ctx.overlayRoot`), not inside it, because M1's overlays
 * rewrite `#overlay` wholesale; modal.css hides the layer while the pause menu shows. UI events only, never per frame.
 */
import type { UiState } from '../../app/feature'
import { enterModal } from '../../app/ui'
import { logger } from '../../core/logger'
import { createPadNav, disarmPadNav, stepPadNav, type MenuInput, type PadNavState } from '../../hud/menu'
import type { PadLike } from '../../input/playerInput'
import { loadSettingsConfig } from '../../settings/settings'
import './modal.css'

export interface ModalButton {
  id: string
  label: string
  /** The default focus (the first primary button; otherwise the first button). Styled as the main action. */
  primary?: boolean
  /** R3-5: shown but not choosable (activate and click do nothing); `reason` is its tooltip. */
  disabled?: boolean
  /** Why the button is disabled (the button's title). */
  reason?: string
}

export interface ModalSpec {
  title: string
  body: string
  buttons: readonly ModalButton[]
  /** False for a dialog that must be answered: 'back' (Esc, pad B, the Esc stack) is then ignored. Default true. */
  dismissable?: boolean
}

// ---------------------------------------------------------------------------------------------
// Pure model
// ---------------------------------------------------------------------------------------------

export type ModalInput = 'prev' | 'next' | 'activate' | 'back'

export interface ModalModel {
  readonly buttons: readonly ModalButton[]
  /** Whether 'back' answers with the last button (`ModalSpec.dismissable`, default true). */
  readonly dismissable: boolean
  /** Index of the focused button. */
  focus: number
  /** The chosen button id once answered (then every input is ignored). */
  result: string | null
}

export function modalModel(spec: ModalSpec): ModalModel {
  if (spec.buttons.length === 0) throw new Error(`Modal "${spec.title}" has no buttons`)
  const ids = new Set(spec.buttons.map((b) => b.id))
  if (ids.size !== spec.buttons.length) throw new Error(`Modal "${spec.title}" has duplicate button ids`)
  const primary = spec.buttons.findIndex((b) => b.primary === true)
  return { buttons: spec.buttons, dismissable: spec.dismissable !== false, focus: primary >= 0 ? primary : 0, result: null }
}

/** Applies one input; returns the chosen id when it answers the dialog (null otherwise). Focus moves wrap. */
export function modalInput(m: ModalModel, input: ModalInput): string | null {
  if (m.result !== null) return null
  const n = m.buttons.length
  switch (input) {
    case 'prev':
      m.focus = (m.focus + n - 1) % n
      return null
    case 'next':
      m.focus = (m.focus + 1) % n
      return null
    case 'activate':
      if (m.buttons[m.focus].disabled === true) return null
      m.result = m.buttons[m.focus].id
      return m.result
    case 'back':
      if (!m.dismissable) return null
      m.focus = n - 1
      m.result = m.buttons[n - 1].id
      return m.result
  }
}

/** Focuses button `index` (pointer hover); false when out of range or already answered. */
export function focusModalButton(m: ModalModel, index: number): boolean {
  if (m.result !== null || index < 0 || index >= m.buttons.length || index === m.focus) return false
  m.focus = index
  return true
}

/** Chooses button `index` (a click); returns its id, or null when out of range or already answered. */
export function chooseModalButton(m: ModalModel, index: number): string | null {
  if (m.result !== null || index < 0 || index >= m.buttons.length || m.buttons[index].disabled === true) return null
  m.focus = index
  m.result = m.buttons[index].id
  return m.result
}

/** The dialog input for a key press (`KeyboardEvent.code`), or null for keys the dialog leaves alone. */
export function modalKey(code: string, shift: boolean): ModalInput | null {
  switch (code) {
    case 'ArrowLeft':
    case 'ArrowUp':
      return 'prev'
    case 'ArrowRight':
    case 'ArrowDown':
      return 'next'
    case 'Tab':
      return shift ? 'prev' : 'next'
    case 'Enter':
    case 'NumpadEnter':
    case 'Space':
      return 'activate'
    case 'Escape':
      return 'back'
    default:
      return null
  }
}

/** The dialog input for a menu-navigation input from the pad (`stepPadNav`); Start is left to the loop. */
export function modalPadInput(input: MenuInput | null): ModalInput | null {
  switch (input) {
    case 'up':
    case 'left':
      return 'prev'
    case 'down':
    case 'right':
      return 'next'
    case 'activate':
      return 'activate'
    case 'back':
      return 'back'
    default:
      return null
  }
}

// ---------------------------------------------------------------------------------------------
// DOM
// ---------------------------------------------------------------------------------------------

function firstPad(): PadLike | null {
  if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return null
  const pads = navigator.getGamepads()
  for (let i = 0; i < pads.length; i++) {
    const p = pads[i]
    if (p?.connected) return p
  }
  return null
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  e.className = className
  if (text !== undefined) e.textContent = text
  return e
}

let dialogCount = 0

/**
 * Shows the dialog and resolves with the chosen button's id. `root` is the overlay layer the dialog sits just above
 * (`ctx.overlayRoot`); pass the game's `UiState` whenever it exists.
 */
export function showModal(root: HTMLElement, spec: ModalSpec, ui?: UiState | null): Promise<string> {
  const model = modalModel(spec)
  return new Promise<string>((resolve) => {
    const id = `reach-modal-${++dialogCount}`
    const layer = element('div', 'reach-modal-layer')
    const dialog = element('div', 'reach-modal')
    dialog.setAttribute('role', 'dialog')
    dialog.setAttribute('aria-modal', 'true')
    dialog.setAttribute('aria-labelledby', `${id}-title`)
    dialog.setAttribute('aria-describedby', `${id}-body`)
    const title = element('h2', 'reach-modal-title', spec.title)
    title.id = `${id}-title`
    const body = element('p', 'reach-modal-body', spec.body)
    body.id = `${id}-body`
    const row = element('div', 'reach-modal-buttons')
    const buttons = spec.buttons.map((b, index) => {
      const button = element('button', b.primary ? 'reach-modal-button primary' : 'reach-modal-button', b.label)
      button.type = 'button'
      button.tabIndex = -1
      button.dataset.id = b.id
      if (b.disabled === true) {
        button.disabled = true
        if (b.reason !== undefined) button.title = b.reason
      }
      button.addEventListener('pointerenter', () => {
        if (focusModalButton(model, index)) sync()
      })
      button.addEventListener('click', () => {
        const chosen = chooseModalButton(model, index)
        if (chosen !== null) finish(chosen)
      })
      row.appendChild(button)
      return button
    })
    dialog.append(title, body, row)
    layer.appendChild(dialog)
    if (root.parentElement) root.insertAdjacentElement('afterend', layer)
    else root.appendChild(layer)

    const sync = (): void => {
      for (let i = 0; i < buttons.length; i++) {
        const focused = i === model.focus
        buttons[i].classList.toggle('focused', focused)
        buttons[i].setAttribute('aria-selected', focused ? 'true' : 'false')
      }
    }
    // Hidden by modal.css while the pause menu covers it: it must not answer keys meant for the menu.
    const covered = (): boolean => getComputedStyle(layer).visibility === 'hidden'

    const apply = (input: ModalInput): void => {
      const chosen = modalInput(model, input)
      if (chosen !== null) finish(chosen)
      else sync()
    }

    const onKey = (e: KeyboardEvent): void => {
      if (model.result !== null || e.defaultPrevented || covered()) return
      const input = modalKey(e.code, e.shiftKey)
      if (input === null) return
      e.preventDefault()
      e.stopPropagation()
      if (!e.repeat || input === 'prev' || input === 'next') apply(input)
    }

    let nav: PadNavState | null = null
    try {
      nav = createPadNav(loadSettingsConfig().menuNav, -1)
      disarmPadNav(nav)
    } catch (err: unknown) {
      logger.warn('Modal pad navigation unavailable', err)
    }
    let raf = 0
    let last = -1
    const padTick = (now: number): void => {
      raf = 0
      if (model.result !== null || !nav) return
      const dt = last < 0 ? 0 : Math.min((now - last) / 1000, 0.25)
      last = now
      const input = modalPadInput(stepPadNav(nav, firstPad(), dt))
      if (input !== null && !covered()) apply(input)
      if (model.result === null) raf = requestAnimationFrame(padTick)
    }

    const leave = ui
      ? enterModal(ui, () => {
          apply('back')
          return true
        })
      : null

    function finish(chosen: string): void {
      window.removeEventListener('keydown', onKey, { capture: true })
      if (raf !== 0) cancelAnimationFrame(raf)
      raf = 0
      layer.remove()
      leave?.()
      resolve(chosen)
    }

    window.addEventListener('keydown', onKey, { capture: true })
    if (nav && typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(padTick)
    sync()
  })
}
